import React, {
    ForwardedRef,
    ChangeEvent,
    KeyboardEvent,
    forwardRef
} from 'react';
import styled from 'styled-components';

import theme from '@styles/theme';
import arrowDownColumn from '@assets/icons/arrowDownColumn.svg';

import { RegularFont, FlexBox, ColorPalette } from './StyledComponents';

type StyledColorPickProps = {
    REQUIRED?: boolean;
    id?: string;
    width?: number;
    height?: number;
    margin?: string;
    border?: string;
    borderRadius?: number;
    title?: string;
    subTitle?: string;
    description?: string;
    name?: string;
    value: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
};

const StyledColorPick = forwardRef(
    (
        {
            REQUIRED,
            id,
            width,
            height = 50,
            margin,
            border,
            borderRadius = 4,
            title,
            subTitle,
            name = '',
            value = '',
            onChange = () => null,
            onKeyUp = () => null,
            onKeyDown = () => null,
            disabled = false
        }: StyledColorPickProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        return (
            <Wrapper $width={width} $margin={margin}>
                {title && (
                    <FlexBox
                        $justifyContent="space-between"
                        $alignItems="center"
                    >
                        <ColumnTitle>
                            {title}
                            {REQUIRED && <Asterisk> *</Asterisk>}
                            {subTitle && <SubTitle>{subTitle}</SubTitle>}
                        </ColumnTitle>
                    </FlexBox>
                )}
                <Label
                    htmlFor={id}
                    $height={height}
                    $border={border}
                    $borderRadius={borderRadius}
                >
                    <Value>
                        <ColorPalette
                            $width={16}
                            $height={16}
                            $margin="0  8px 0 0"
                            $bgColor={value}
                        />
                        {value.toLocaleUpperCase()}
                    </Value>
                    <Input
                        id={id}
                        type="color"
                        $height={height}
                        ref={ref}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onKeyUp={onKeyUp}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                    />
                </Label>
            </Wrapper>
        );
    }
);

export default React.memo(StyledColorPick);

const Wrapper = styled.div<{
    $width?: number;
    $margin?: string;
}>`
    display: block;
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
`;

const ColumnTitle = styled(RegularFont)`
    margin-bottom: 8px;
    font-size: 14px;
`;

const Asterisk = styled.span`
    color: ${theme.colors.activeOrange};
`;

const SubTitle = styled.span`
    margin-left: 8px;
    color: ${theme.colors.lightGrayFontColor};
`;
const Value = styled.div`
    position: absolute;
    left: 12px;
    top: 12px;
    display: flex;
    align-items: center;
`;
const Label = styled.label<{
    $width?: number;
    $height?: number;
    $border?: string;
    $borderRadius?: number;
}>`
    position: relative;
    display: inline-block;
    padding: 14px 12px;
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    height: ${({ $height }) => $height}px;
    font-size: 16px;
    font-family: PretendardRegular, sans-serif;
    border: ${({ $border }) =>
        $border ? $border : `1px solid ${theme.colors.lightGrayBorder}`};
    border-radius: ${({ $borderRadius }) => $borderRadius}px;
    background-position: bottom 50% right 12px;
    background-image: url(${arrowDownColumn});
    background-size: 16px;
    background-repeat: no-repeat;
`;
const Input = styled.input<{
    $width?: number;
    $height?: number;
}>`
    position: absolute;
    top: 0;
    left: 0;
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    height: ${({ $height }) => $height}px;
    opacity: 0;
`;
